import { Entity } from "@rest-hooks/rest";
import AuthdEndpoint from "./AuthdEndpoint";
import { BASE_URL } from "./constants";

export type ModerationAction = "FLAGGED" | "DELETED";

export class ModerationFlag extends Entity {
  id = "";
  meetingId = "";
  messageId = "";
  senderId = "";
  senderName = "";
  content = "";
  action = "" as ModerationAction;
  categories: string[] = [];
  confidence = 0;
  reason = "";
  createdAt = "";
  meeting?: {
    id: string;
    title: string;
    externalMeetingId: string;
  };

  pk(): string | undefined {
    return this.id;
  }
}

export interface Paging {
  count: number;
  total?: number;
  limit: number;
  nextToken?: string | null;
}

export interface ModerationConfig {
  enabled: boolean;
  deleteThreshold: number;
  flagThreshold: number;
  categories?: {
    SEXUAL?: {
      deleteThreshold: number;
      flagThreshold: number;
    };
  };
  timeoutMs: number;
  language: string;
}

export interface DeploymentStatus {
  state:
    | "DEPLOYING"
    | "BAKING"
    | "COMPLETE"
    | "ROLLING_BACK"
    | "ROLLED_BACK"
    | "UNKNOWN";
  deploymentNumber?: number;
  configurationVersion?: string;
  percentageComplete?: number;
  startedAt?: string;
  completedAt?: string;
}

export interface ModerationConfigResponse {
  config: ModerationConfig;
  deployment: DeploymentStatus;
}

export interface BlocklistItem {
  type: "emojis" | "emoticons" | "words";
  value: string;
}

export interface Blocklist {
  emojis: string[];
  emoticons: string[];
  words: string[];
}

export interface Allowlist {
  words: string[];
}

// GET /v1/admin/chat/moderation/flags - List moderation flags
export const moderationFlagsEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/chat/moderation/flags",
  schema: {
    data: [ModerationFlag],
    paging: {} as Paging,
  },
  searchParams: {} as {
    meetingId?: string;
    action?: ModerationAction;
    limit?: number;
    nextToken?: string;
  },
});

// GET /v1/admin/chat/moderation/config - Get moderation config
export const moderationConfigGetEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/chat/moderation/config",
  schema: {} as ModerationConfigResponse,
});

// PUT /v1/admin/chat/moderation/config - Update moderation config
export const moderationConfigUpdateEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/chat/moderation/config",
  method: "PUT",
  schema: {} as ModerationConfig,
});

// GET /v1/admin/chat/moderation/blocklist - Get blocklist
export const moderationBlocklistGetEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/chat/moderation/blocklist",
  schema: {} as Blocklist,
});

// POST /v1/admin/chat/moderation/blocklist - Add item to blocklist
export const moderationBlocklistAddEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/chat/moderation/blocklist",
  method: "POST",
  schema: {} as { success: boolean },
});

// DELETE /v1/admin/chat/moderation/blocklist - Remove item from blocklist
export const moderationBlocklistDeleteEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/chat/moderation/blocklist",
  method: "DELETE",
  schema: {} as { success: boolean },
});

// GET /v1/admin/chat/moderation/allowlist - Get allowlist
export const moderationAllowlistGetEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/chat/moderation/allowlist",
  schema: {} as Allowlist,
});

// POST /v1/admin/chat/moderation/allowlist - Add word to allowlist
export const moderationAllowlistAddEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/chat/moderation/allowlist",
  method: "POST",
  schema: {} as { success: boolean },
});

// DELETE /v1/admin/chat/moderation/allowlist - Remove word from allowlist
export const moderationAllowlistDeleteEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/chat/moderation/allowlist",
  method: "DELETE",
  schema: {} as { success: boolean },
});

export const ModerationResource = {
  getFlags: moderationFlagsEndpoint,
  getConfig: moderationConfigGetEndpoint,
  updateConfig: moderationConfigUpdateEndpoint,
  getBlocklist: moderationBlocklistGetEndpoint,
  addBlocklistItem: moderationBlocklistAddEndpoint,
  deleteBlocklistItem: moderationBlocklistDeleteEndpoint,
  getAllowlist: moderationAllowlistGetEndpoint,
  addAllowlistWord: moderationAllowlistAddEndpoint,
  deleteAllowlistWord: moderationAllowlistDeleteEndpoint,
};
